/**
 * @fileoverview Ad-hoc violations module (formKind === 'module', moduleType === 'violations').
 * @module inspection-renderer/modules
 *
 * Responsibilities:
 * - Render the inspector-facing ad-hoc violation capture surface (G-002):
 *   search code sets, log violations that have no template question.
 * - All ad-hoc captures persist with `source: 'additional'` under the
 *   sentinel form id `ADDITIONAL_VIOLATION_FORM_ID` (Phase 5 adapter).
 *
 * Dependencies:
 * - React 18 (peer).
 * - `ViolationCaptureBlock` for the per-violation surface.
 * - `@firesquirrel/core-domain` `ViolationsModuleConfig`.
 *
 * Architecture:
 * - Presentation-only. The host's PersistAdapter owns the list; this
 *   component is purely a controlled view.
 *
 * @component ViolationsModule
 *
 * @contract ViolationsModuleProps
 * Purpose: prop contract for the ad-hoc violations module.
 */
import { useState, type ReactNode } from 'react';
import type { ViolationsModuleConfig } from '@firesquirrel/core-domain';
import type { LoggedViolation } from '../violations/types';

export interface ViolationsModuleProps {
  config: ViolationsModuleConfig;
  /** Existing additional-source violations (already captured). */
  violations: LoggedViolation[];
  /** Bulk-pilot lock — module renders read-only. */
  readOnly?: boolean;
  /** Persist a new / updated / removed violation. */
  onChange: (next: LoggedViolation[]) => void;
}

export function ViolationsModule(props: ViolationsModuleProps): ReactNode {
  const { config, violations, readOnly, onChange } = props;
  const [query, setQuery] = useState('');
  const [draftCode, setDraftCode] = useState('');
  const [draftNotes, setDraftNotes] = useState('');

  function logAdHoc() {
    if (readOnly || !draftCode.trim()) return;
    const id = `loc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const next: LoggedViolation = {
      _id: id,
      fieldId: null,
      formId: 'ADDITIONAL_VIOLATION_FORM_ID',
      codes: [{ code: draftCode.trim(), codeSetId: config.codeSetIds[0] ?? 'unknown' }],
      severity: 'major',
      status: 'open',
      discoveredInEventIndex: 0,
      source: 'additional',
      occurrences: [{
        _id: `${id}-occ`,
        status: 'open',
        discoveredInEventIndex: 0,
        attachments: [],
        discoveredAt: new Date().toISOString(),
        discoveredBy: 'inspector',
        notes: draftNotes.trim() || undefined,
      }],
    };
    onChange([...violations, next]);
    setDraftCode('');
    setDraftNotes('');
  }

  function remove(id: string) {
    if (readOnly) return;
    onChange(violations.filter((v) => v._id !== id));
  }

  return (
    <div
      data-module-type="violations"
      data-read-only={readOnly ? 'true' : 'false'}
      data-allow-ai-search={config.allowAiSearch ? 'true' : 'false'}
      className="border border-gray-200 rounded-sm p-3"
    >
      <h3 className="text-sm font-semibold text-gray-800 mb-2">Additional violations</h3>
      {!readOnly && (
        <div className="grid grid-cols-1 gap-2">
          {config.allowAiSearch && (
            <input
              type="search"
              placeholder="Search code sets…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-action="search"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm"
            />
          )}
          <input
            type="text"
            placeholder="Code (e.g. 906.1)"
            value={draftCode}
            onChange={(e) => setDraftCode(e.target.value)}
            data-action="draft-code"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm"
          />
          {config.showNotes && (
            <textarea
              placeholder="Notes"
              value={draftNotes}
              onChange={(e) => setDraftNotes(e.target.value)}
              rows={2}
              aria-invalid={config.requireNotes && !draftNotes.trim()}
              data-action="draft-notes"
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded-sm resize-none"
            />
          )}
          <button
            type="button"
            onClick={logAdHoc}
            disabled={!draftCode.trim() || (config.requireNotes && !draftNotes.trim())}
            data-action="log-ad-hoc"
            className="self-start px-3 py-1.5 text-xs font-semibold bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            Log Violation
          </button>
        </div>
      )}

      {violations.length > 0 && (
        <ul role="list" data-additional-violations className="mt-3 flex flex-col gap-1.5 border-t border-gray-200 pt-2">
          {violations.map((v) => (
            <li key={v._id} role="listitem" data-violation-id={v._id} className="flex items-start justify-between gap-2 text-xs">
              <div>
                <code className="font-mono text-gray-700">{v.codes[0]?.codeSetId} / {v.codes[0]?.code}</code>
                {v.occurrences[0]?.notes && <div className="text-gray-600">{v.occurrences[0].notes}</div>}
              </div>
              {!readOnly && (
                <button type="button" onClick={() => remove(v._id)} className="text-xs text-red-500 hover:text-red-700">
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
